import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  X,
  CircleNotch,
  ShieldCheck,
  Eye,
  Warning,
  Lightning,
  Robot,
  Plugs,
  CaretDown,
  ArrowLeft,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════
   HUMANIZED LABELS
   ═══════════════════════════════════════════ */

const TOOLKIT_LABELS = {
  hubspot: 'HubSpot', slack: 'Slack', salesforce: 'Salesforce',
  googlesheets: 'Google Sheets', sendgrid: 'SendGrid', discord: 'Discord',
  microsoft_teams: 'Microsoft Teams', calendly: 'Calendly', gmail: 'Gmail',
  outlook: 'Outlook', airtable: 'Airtable', twilio: 'Twilio',
};

const CAP_LABELS = {
  cap_send_email: 'Send email', cap_reply_email: 'Reply to email',
  cap_read_email: 'Read email', cap_create_event: 'Create calendar event',
  cap_check_availability: 'Check availability', cap_create_contact: 'Create contact',
  cap_update_contact: 'Update contact', cap_get_contact: 'Get contact',
  cap_search_contact: 'Search contacts', cap_log_activity: 'Log activity',
  cap_send_sms: 'Send SMS', cap_send_chat_message: 'Send message',
  cap_reschedule_event: 'Reschedule event', cap_cancel_event: 'Cancel event',
  cap_manage_labels: 'Manage labels',
};

function humanAction(action) {
  if (!action) return '';
  // HUBSPOT_CREATE_CONTACT → Create contact
  const parts = action.split('_');
  const withoutTk = parts.slice(1).join(' ');
  return withoutTk.charAt(0).toUpperCase() + withoutTk.slice(1).toLowerCase();
}

function humanCap(capId) {
  return CAP_LABELS[capId] || capId?.replace(/^cap_/, '').replace(/_/g, ' ') || '';
}

function toolkitLabel(slug) {
  return TOOLKIT_LABELS[slug] || slug?.charAt(0).toUpperCase() + slug?.slice(1) || '';
}

/* ═══════════════════════════════════════════
   STATUS CONFIG
   ═══════════════════════════════════════════ */

const STATUS_CONFIG = {
  auto_applied: {
    label: 'Active',
    color: '#00E5C0',
    bg: 'rgba(0,229,192,0.06)',
    border: 'rgba(0,229,192,0.12)',
    Icon: ShieldCheck,
    description: 'High confidence — automatically active',
  },
  confirmed: {
    label: 'Confirmed',
    color: '#00E5C0',
    bg: 'rgba(0,229,192,0.06)',
    border: 'rgba(0,229,192,0.12)',
    Icon: Check,
    description: 'You confirmed this mapping',
  },
  suggested: {
    label: 'Needs review',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.06)',
    border: 'rgba(245,158,11,0.12)',
    Icon: Eye,
    description: 'Medium confidence — confirm to activate',
  },
  requires_confirmation: {
    label: 'Needs review',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.06)',
    border: 'rgba(245,158,11,0.12)',
    Icon: Warning,
    description: 'Low confidence — review before activating',
  },
  rejected: {
    label: 'Rejected',
    color: '#7D8896',
    bg: 'rgba(255,255,255,0.02)',
    border: 'rgba(255,255,255,0.04)',
    Icon: X,
    description: 'Not used for execution',
  },
};

const CONFIDENCE_LABELS = {
  high: { label: 'High', color: '#00E5C0' },
  medium: { label: 'Medium', color: '#F59E0B' },
  low: { label: 'Low', color: '#EF4444' },
};

function confidenceLevel(score) {
  if (score >= 0.8) return 'high';
  if (score >= 0.5) return 'medium';
  return 'low';
}

/* ═══════════════════════════════════════════
   MAPPING CARD
   ═══════════════════════════════════════════ */

function MappingCard({ mapping, onConfirm, onReject, confirming }) {
  const status = STATUS_CONFIG[mapping.status] || STATUS_CONFIG.suggested;
  const confLevel = confidenceLevel(mapping.combined_confidence || 0);
  const conf = CONFIDENCE_LABELS[confLevel];
  const workers = mapping.workers_using || [];
  const isActionable = mapping.status === 'suggested' || mapping.status === 'requires_confirmation';
  const isRejected = mapping.status === 'rejected';

  return (
    <div
      className={`rounded-lg border transition-colors ${
        isRejected ? 'opacity-50 border-[rgba(255,255,255,0.03)] bg-[#0A0C10]' : 'border-[rgba(255,255,255,0.05)] bg-[#1F2937] hover:border-[rgba(255,255,255,0.08)]'
      }`}
      data-testid={`mapping-card-${mapping.mapping_id}`}
    >
      <div className="px-4 py-3.5">
        {/* Flow: Action → Capability → Workers */}
        <div className="flex items-center gap-2 mb-2.5">
          {/* Tool action */}
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[13px] font-medium text-[#F0F4F8] truncate" data-testid="mapping-action">
              {humanAction(mapping.composio_action)}
            </span>
          </div>

          <ArrowRight size={12} className="text-[#2A2E35] shrink-0" />

          {/* Capability */}
          <span className="text-[13px] text-[#9CA3B0] truncate" data-testid="mapping-capability">
            {humanCap(mapping.capability_id)}
          </span>
        </div>

        {/* Status + confidence + workers row */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status badge */}
          <span
            className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
            style={{ color: status.color, background: status.bg, border: `1px solid ${status.border}` }}
            data-testid="mapping-status-badge"
          >
            <status.Icon size={10} weight="fill" />
            {status.label}
          </span>

          {/* Confidence */}
          <span className="text-[10px] flex items-center gap-1" style={{ color: conf.color }} data-testid="mapping-confidence">
            {conf.label} confidence
          </span>

          {/* Workers using */}
          {workers.length > 0 && (
            <span className="text-[10px] text-[#5C6778] flex items-center gap-1">
              <Robot size={10} />
              {workers.length === 1 ? workers[0].name : `${workers.length} workers`}
            </span>
          )}

          {/* Actions */}
          {isActionable && (
            <div className="ml-auto flex items-center gap-1.5">
              <button
                onClick={() => onConfirm(mapping.mapping_id)}
                disabled={confirming === mapping.mapping_id}
                className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.14)] disabled:opacity-40 transition-colors"
                data-testid={`confirm-btn-${mapping.mapping_id}`}
              >
                {confirming === mapping.mapping_id ? <CircleNotch size={10} className="animate-spin" /> : <Check size={10} />}
                Confirm
              </button>
              <button
                onClick={() => onReject(mapping.mapping_id)}
                disabled={confirming === mapping.mapping_id}
                className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded text-[#7D8896] border border-[rgba(255,255,255,0.08)] hover:text-[#9CA3B0] hover:border-[rgba(255,255,255,0.12)] disabled:opacity-40 transition-colors"
                data-testid={`reject-btn-${mapping.mapping_id}`}
              >
                <X size={10} />
                Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TOOLKIT GROUP
   ═══════════════════════════════════════════ */

function ToolkitGroup({ toolkit, mappings, onConfirm, onReject, confirming }) {
  const [collapsed, setCollapsed] = useState(false);
  const active = mappings.filter(m => m.status === 'auto_applied' || m.status === 'confirmed').length;
  const needsReview = mappings.filter(m => m.status === 'suggested' || m.status === 'requires_confirmation').length;

  return (
    <div className="mb-4" data-testid={`toolkit-group-${toolkit}`}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#0B0E13] border border-[rgba(255,255,255,0.04)] hover:border-[rgba(255,255,255,0.08)] transition-colors"
        data-testid={`toolkit-header-${toolkit}`}
      >
        <CaretDown
          size={12}
          className={`text-[#5C6778] transition-transform ${collapsed ? '-rotate-90' : ''}`}
        />
        <span className="text-sm font-medium text-[#F0F4F8]">{toolkitLabel(toolkit)}</span>
        <div className="flex items-center gap-2 ml-auto">
          {active > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(0,229,192,0.06)] text-[#00E5C0] border border-[rgba(0,229,192,0.1)]">
              {active} active
            </span>
          )}
          {needsReview > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(245,158,11,0.06)] text-[#F59E0B] border border-[rgba(245,158,11,0.1)]">
              {needsReview} to review
            </span>
          )}
        </div>
      </button>

      {!collapsed && (
        <div className="mt-1.5 space-y-1.5 pl-3">
          {mappings.map(m => (
            <MappingCard
              key={m.mapping_id}
              mapping={m}
              onConfirm={onConfirm}
              onReject={onReject}
              confirming={confirming}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════ */

export default function MappingDashboard() {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState([]);
  const [byToolkit, setByToolkit] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, review

  const fetchMappings = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/capability-mappings/dashboard`);
      const data = await res.json();
      setMappings(data.mappings || []);
      setByToolkit(data.by_toolkit || {});
    } catch {
      toast.error('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMappings(); }, [fetchMappings]);

  const handleConfirm = async (mappingId) => {
    setConfirming(mappingId);
    try {
      await fetchWithOrg(`${BACKEND_URL}/api/capability-mappings/${mappingId}/confirm`, { method: 'PUT' });
      toast.success('Mapping confirmed — now active');
      fetchMappings();
    } catch {
      toast.error('Failed to confirm mapping');
    } finally {
      setConfirming(null);
    }
  };

  const handleReject = async (mappingId) => {
    setConfirming(mappingId);
    try {
      await fetchWithOrg(`${BACKEND_URL}/api/capability-mappings/${mappingId}/reject`, { method: 'PUT' });
      toast.success('Mapping rejected');
      fetchMappings();
    } catch {
      toast.error('Failed to reject mapping');
    } finally {
      setConfirming(null);
    }
  };

  // Filter mappings
  const filteredByToolkit = {};
  for (const [tk, maps] of Object.entries(byToolkit)) {
    const filtered = maps.filter(m => {
      if (filter === 'active') return m.status === 'auto_applied' || m.status === 'confirmed';
      if (filter === 'review') return m.status === 'suggested' || m.status === 'requires_confirmation';
      return true;
    });
    if (filtered.length > 0) filteredByToolkit[tk] = filtered;
  }

  const totalActive = mappings.filter(m => m.status === 'auto_applied' || m.status === 'confirmed').length;
  const totalReview = mappings.filter(m => m.status === 'suggested' || m.status === 'requires_confirmation').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080A0E] flex items-center justify-center">
        <CircleNotch size={24} className="text-[#00E5C0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080A0E]" data-testid="mapping-dashboard-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Back nav */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate('/workers')}
            data-testid="back-to-workers-btn"
            className="flex items-center gap-1 text-[#7D8896] hover:text-[#B0B8C4] text-xs transition-colors"
          >
            <ArrowLeft size={14} />
            Workers
          </button>
        </div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2.5 mb-1">
            <Plugs size={20} weight="duotone" className="text-[#00E5C0]" />
            <h1 className="text-xl font-semibold text-[#F0F4F8]" data-testid="mapping-dashboard-title">
              How your AI uses your tools
            </h1>
          </div>
          <p className="text-sm text-[#7D8896]">
            Each connected tool is mapped to capabilities your workers can use.
            Only active mappings are used during execution.
          </p>
        </div>

        {/* Summary bar */}
        <div className="flex items-center gap-4 mb-6 px-4 py-3 rounded-lg bg-[#0B0E13] border border-[rgba(255,255,255,0.04)]" data-testid="mapping-summary">
          <div className="flex items-center gap-1.5">
            <Lightning size={14} weight="fill" className="text-[#00E5C0]" />
            <span className="text-xs text-[#F0F4F8] font-medium">{totalActive}</span>
            <span className="text-xs text-[#7D8896]">active</span>
          </div>
          {totalReview > 0 && (
            <div className="flex items-center gap-1.5">
              <Eye size={14} className="text-[#F59E0B]" />
              <span className="text-xs text-[#F0F4F8] font-medium">{totalReview}</span>
              <span className="text-xs text-[#7D8896]">to review</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Plugs size={14} className="text-[#7D8896]" />
            <span className="text-xs text-[#F0F4F8] font-medium">{Object.keys(byToolkit).length}</span>
            <span className="text-xs text-[#7D8896]">tools</span>
          </div>

          {/* Filter tabs */}
          <div className="ml-auto flex items-center gap-1 bg-[#080A0E] p-0.5 rounded-md border border-[rgba(255,255,255,0.03)]">
            {[
              { id: 'all', label: 'All' },
              { id: 'active', label: 'Active' },
              { id: 'review', label: 'Review' },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                  filter === f.id
                    ? 'bg-[#161C24] text-[#F0F4F8]'
                    : 'text-[#5C6778] hover:text-[#7D8896]'
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Toolkit groups */}
        {Object.keys(filteredByToolkit).length === 0 ? (
          <div className="text-center py-16 border border-[rgba(255,255,255,0.04)] rounded-lg bg-[#1F2937]" data-testid="mapping-empty-state">
            <Plugs size={28} className="text-[#2A2E35] mx-auto mb-2" />
            <p className="text-sm text-[#5C6778]">
              {filter === 'review'
                ? 'No mappings to review'
                : mappings.length === 0
                ? 'No tool mappings yet'
                : 'No mappings match this filter'}
            </p>
            <p className="text-[10px] text-[#2A2E35] mt-1">
              Connect integrations to see how your AI uses them
            </p>
          </div>
        ) : (
          <div data-testid="mapping-toolkit-list">
            {Object.entries(filteredByToolkit).map(([tk, maps]) => (
              <ToolkitGroup
                key={tk}
                toolkit={tk}
                mappings={maps}
                onConfirm={handleConfirm}
                onReject={handleReject}
                confirming={confirming}
              />
            ))}
          </div>
        )}

        {/* Explanation footer */}
        <div className="mt-8 px-4 py-3 rounded-lg border border-[rgba(255,255,255,0.03)] bg-[#0A0C10]" data-testid="mapping-explanation">
          <p className="text-[10px] text-[#5C6778] leading-relaxed">
            <span className="text-[#7D8896] font-medium">How this works:</span> When you connect a tool,
            the platform analyzes its available actions and maps them to worker capabilities.
            High-confidence mappings are activated automatically. Medium and low confidence mappings
            require your confirmation before workers can use them.
          </p>
        </div>
      </div>
    </div>
  );
}
